/** 
 * Triplin Premium Design Tokens
 * Inspired by Airbnb, Booking.com, Stripe, and Apple
 */

export const brand = {
  // Primary Blue
  blue50: '#EFF6FF',
  blue100: '#DBEAFE',
  blue200: '#BFDBFE',
  blue300: '#93C5FD',
  blue400: '#60A5FA',
  blue500: '#3B82F6',
  blue600: '#2563EB',
  blue700: '#1D4ED8',
  blue800: '#1E40AF',
  blue900: '#1E3A8A',

  // Dark Navy (for footer, dark sections)
  navy: '#0F172A',
  navyLight: '#1E293B',
  navyMid: '#334155',

  // Backgrounds
  background: '#F8FAFC',
  backgroundAlt: '#F1F5F9',
  white: '#FFFFFF',

  // Borders
  border: '#E2E8F0',
  borderLight: '#F1F5F9',
  borderDark: '#CBD5E1',

  // Text
  textPrimary: '#111827',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  textLight: '#CBD5E1',

  // Semantic
  success: '#16A34A',
  successLight: '#DCFCE7',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#DC2626',
  errorLight: '#FEE2E2',
  info: '#0EA5E9',
  infoLight: '#E0F2FE',

  // Gold accent (for ratings, premium badges)
  gold: '#F59E0B',
  goldDark: '#D97706',
  goldLight: '#FEF3C7',
  goldXlight: '#FFFBEB',
  
  // Legacy tokens (for backwards compatibility)
  sageDeep: '#2563EB',
  sageDark: '#1D4ED8',
  sageMid: '#BFDBFE',
  sageLight: '#DBEAFE',
  sageXlight: '#EFF6FF',
  cream: '#F8FAFC',
  creamWarm: '#F1F5F9',
  creamMid: '#E2E8F0',
  successBg: '#DCFCE7',
  warningBg: '#FEF3C7',
  errorBg: '#FEE2E2',
} as const;

export const colors = {
  primary: {
    50: brand.blue50,
    100: brand.blue100,
    200: brand.blue200,
    300: brand.blue300,
    400: brand.blue400,
    500: brand.blue500,
    600: brand.blue600,
    700: brand.blue700,
    800: brand.blue800,
    900: brand.blue900,
  },
  secondary: {
    50: brand.goldLight,
    100: brand.goldLight,
    200: brand.goldLight,
    300: brand.gold,
    400: brand.gold,
    500: brand.gold,
    600: brand.goldDark,
    700: brand.goldDark,
  },
  success: brand.success,
  warning: brand.warning,
  error: brand.error,
  info: brand.info,
  neutral: {
    50: brand.background,
    100: brand.backgroundAlt,
    200: brand.border,
    300: brand.borderDark,
    400: brand.textLight,
    500: brand.textMuted,
    600: brand.textSecondary,
    700: brand.navyMid,
    800: brand.navyLight,
    900: brand.navy,
  },
  background: {
    page: brand.background,
    sectionAlt: brand.backgroundAlt,
    card: brand.white,
    input: brand.white,
    footer: brand.navy,
    overlay: 'rgba(15, 23, 42, 0.6)',
  },
  text: {
    heading: brand.textPrimary,
    body: brand.textSecondary,
    secondary: brand.textMuted,
    disabled: brand.textLight,
    white: brand.white,
  },
} as const;

export const spacing = {
  0.5: 2,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  30: 120,
} as const;

export const radii = {
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '10px',
  '2xl': '12px',
  /** Prefer boxy corners — avoid pill shapes for UI chrome */
  full: '8px',
} as const;

export const shadows = {
  xs: '0 1px 2px rgba(15, 23, 42, 0.05)',
  sm: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  md: '0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -1px rgba(15, 23, 42, 0.04)',
  lg: '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.03)',
  xl: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 10px 10px -5px rgba(15, 23, 42, 0.04)',
  '2xl': '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
  card: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
  cardHover: '0 20px 40px -8px rgba(15, 23, 42, 0.12)',
  hover: '0 20px 40px -8px rgba(15, 23, 42, 0.12)',
  search: '0 8px 30px rgba(15, 23, 42, 0.12)',
  modal: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
  glass: '0 8px 32px rgba(15, 23, 42, 0.1)',
} as const;

export const motion = {
  duration: 0.2,
  durationSlow: 0.3,
  cardHoverY: -4,
  buttonScale: 1.02,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easingOut: 'cubic-bezier(0, 0, 0.2, 1)',
} as const;

export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export const typography = {
  fontDisplay: '"Playfair Display", "Noto Serif Devanagari", Georgia, serif',
  fontBody: '"Inter", "Noto Sans Devanagari", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
} as const;
